import React from "react"
import PropTypes from "prop-types"
import { Flex, Typography, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { CHART_COLORS, lineChartOptions } from "../../constants"
import { formatValue, markdownToNode } from "../../utils"
import EmptyState from "components/common/EmptyState"
import styles from "../../style.module.css"

const { Text } = Typography

export default function RevenueTrendWidget({ data, description, title, value_format }) {
  const { t } = useTranslation()
  // chart_data for line: { labels, series: [{ name, data }] }
  const labels = data?.labels ?? []
  const series = Array.isArray(data?.series) ? data.series : []
  const values = series[0]?.data ?? []
  const total = values.reduce((sum, v) => sum + (v ?? 0), 0)

  const chartData = {
    labels,
    datasets: series.map((s, i) => ({
      label: s.name,
      data: s.data,
      borderColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
      backgroundColor: i === 0 ? `${CHART_COLORS.blue}14` : `${CHART_COLORS.amber}14`,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: i === 0 ? CHART_COLORS.blue : CHART_COLORS.amber,
      pointRadius: labels.length > 30 ? 2 : 4,
      pointHoverRadius: 6,
      spanGaps: false
    }))
  }

  const chartOptions = {
    ...lineChartOptions,
    plugins: {
      ...lineChartOptions.plugins,
      tooltip: {
        ...lineChartOptions.plugins.tooltip,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatValue(ctx.parsed.y, value_format)}`
        }
      }
    },
    scales: {
      ...lineChartOptions.scales,
      y: {
        ...lineChartOptions.scales.y,
        ticks: {
          ...lineChartOptions.scales.y.ticks,
          callback: (value) => formatValue(value, value_format)
        }
      }
    }
  }

  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="center" className={styles.widgetHeader}>
        <Text strong>{title ?? t("ai_dashboard.widget_revenue_trend")}</Text>
        {description && (
          <Tooltip title={markdownToNode(description)} placement="topRight" styles={{ body: { maxWidth: 320 } }}>
            <InfoCircleOutlined className={styles.widgetInfoIcon} />
          </Tooltip>
        )}
      </Flex>
      <Text type="secondary" className={styles.textPeriodInfo}>{formatValue(total, value_format)} {t("ai_dashboard.during_period")}</Text>
      <div className={styles.chartWrapperMd}>
        {labels.length > 0
          ? <Line data={chartData} options={chartOptions} />
          : <div className={styles.widgetEmptyState}><EmptyState title={t("ai_dashboard.no_data")} description=" " /></div>
        }
      </div>
    </div>
  )
}

RevenueTrendWidget.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  description: PropTypes.string,
  title: PropTypes.string,
  value_format: PropTypes.string,
}
